/**
 * ShelfCard — shared entity-card primitive for the 5 shelves.
 *
 * Phase 6 §"Sub-entities render as CARDS (not list items) inside the
 * opened shelf — bigger, with badges + status + last-touched". One
 * card primitive, used by every shelf so the visual rhythm matches.
 *
 * The card is a NavLink — click navigates; hover gets a soft slate
 * background. No icon-badge sea-of-color (Sidebar.tsx rework
 * principle carries forward).
 */

import { NavLink, useLocation } from "react-router-dom";
import { preserveMode } from "@/hooks/useProjectMode";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { TYPE } from "@/lib/typography";
import { StatusPill } from "@/components/ui/StatusDot";

export interface ShelfCardProps {
  to: string;
  label: string;
  status?: string;
  count?: number;
  lastTouched?: string;
  /** Optional pre-formatted slug for tests (e.g. ``card-objectives``). */
  testId?: string;
  /** Felix-spoken label uses Phase 1 serif italic; nav cards stay
   *  sans. */
  felixSpoken?: boolean;
  /** The surface this card names does not exist yet.
   *
   *  Renders a muted, non-navigating card instead of a link. Six of the
   *  nineteen shelf cards pointed at routes with no page — Plan/Decisions,
   *  Design/Wireframes, Design/Mockups, Execute/Impediments, Govern/Approvals,
   *  Govern/Audit Log — so a third of the Orchestrator's front door 404'd, two
   *  of them on Govern, the shelf a sponsor is most likely to open.
   *
   *  Marking them rather than deleting them keeps the shelf honest about the
   *  intended shape of the product without sending anyone to a dead page. A
   *  404 is worse than an absence; an absence with a reason is better than
   *  either. */
  unavailable?: boolean;
}

export function ShelfCard({
  to,
  label,
  status,
  count,
  lastTouched,
  testId,
  felixSpoken = false,
  unavailable = false,
}: ShelfCardProps) {
  const resolvedTestId = testId ?? `shelf-card-${label.toLowerCase()}`;
  // Carry the mode forward. Without this the first card click erases the choice
  // made on the landing page and the shelf sidebar this card lives in unmounts
  // underneath the user.
  const { search } = useLocation();

  if (unavailable) {
    return (
      <motion.div layout transition={{ duration: 0.18 }}>
        <div
          data-testid={resolvedTestId}
          data-unavailable="true"
          aria-disabled="true"
          className={cn(
            "group block cursor-not-allowed rounded-xl border p-3",
            "border-dashed border-[var(--color-border)]",
            "bg-[var(--color-surface)] opacity-60",
          )}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                TYPE.bodyMed,
                "truncate text-[var(--color-text-secondary)]",
                felixSpoken && "font-serif italic",
              )}
            >
              {label}
            </span>
          </div>
          <span className={cn(TYPE.meta, "text-[var(--color-text-tertiary)]")}>
            Not yet available
          </span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div layout transition={{ duration: 0.18 }}>
      <NavLink
        to={preserveMode(to, search)}
        data-testid={testId ?? `shelf-card-${label.toLowerCase()}`}
        className={({ isActive }) =>
          cn(
            "group block rounded-xl border p-3 transition-colors",
            "border-[var(--color-border)] bg-[var(--color-surface)]",
            "hover:bg-[var(--color-surface-secondary)]",
            isActive && "border-brand-500 ring-1 ring-brand-500/30",
          )
        }
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              TYPE.bodyMed,
              "truncate text-[var(--color-text-primary)]",
              felixSpoken && "font-serif italic",
            )}
          >
            {label}
          </span>
          {typeof count === "number" && (
            <span
              className={cn(
                TYPE.badge,
                "rounded-md bg-[var(--color-surface-secondary)] px-1.5 py-0.5 text-[var(--color-text-secondary)]",
              )}
              aria-label={`${count} items`}
            >
              {count}
            </span>
          )}
        </div>
        {(status || lastTouched) && (
          <div className={cn(TYPE.meta, "mt-1 flex items-center gap-2")}>
            {status && (
              <span
                data-testid={`${testId ?? `shelf-card-${label.toLowerCase()}`}-status`}
              >
                <StatusPill status={status} label={status} />
              </span>
            )}
            {lastTouched && <span>{lastTouched}</span>}
          </div>
        )}
      </NavLink>
    </motion.div>
  );
}
